// The pieces every sidebar row shares: the row's look, the icon on its tile,
// and the label.
//
// The tile, the grey label and the current page in the primary colour are the
// sidebar's look from before the 2026-10-01 redesign, kept on request. The row
// around them follows the redesign: full width, the current page tinted with a
// bar on its right edge.
import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

const ROW_GUTTER = 20;
const ICON_TILE = 36;
const ICON_GAP = 12;
/** Where a dropdown's pages start — under their parent's label, not its icon. */
export const NESTED_INDENT = ROW_GUTTER + ICON_TILE + ICON_GAP;

export const navRowSx = (theme, { expanded, nested = false }) => ({
  minHeight: nested ? 40 : 44,
  py: 0.5,
  pl: expanded ? `${nested ? NESTED_INDENT : ROW_GUTTER}px` : 0,
  pr: expanded ? `${ROW_GUTTER}px` : 0,
  gap: `${ICON_GAP}px`,
  justifyContent: expanded ? 'flex-start' : 'center',
  color: 'text.secondary',
  borderRight: '2px solid transparent',
  transition: 'background-color 120ms ease',
  '&:hover': { bgcolor: 'action.hover' },
  '&.Mui-selected': {
    bgcolor: alpha(theme.palette.primary.main, 0.08),
    borderRightColor: 'primary.main',
    color: 'primary.main',
  },
  '&.Mui-selected:hover': { bgcolor: alpha(theme.palette.primary.main, 0.12) },
  '&.Mui-disabled': { opacity: 0.5 },
});

/** The icon on its tinted tile; `dot` marks a count the collapsed row cannot spell out. */
export function IconTile({ icon, highlighted, dot = false }) {
  return (
    <Box
      sx={(theme) => ({
        position: 'relative',
        width: ICON_TILE,
        height: ICON_TILE,
        flexShrink: 0,
        borderRadius: 1.5,
        display: 'grid',
        placeItems: 'center',
        bgcolor: alpha(theme.palette.primary.main, highlighted ? 0.16 : 0.08),
        boxShadow: highlighted ? '0 6px 14px rgba(15, 23, 42, 0.12)' : 'none',
        color: highlighted ? 'primary.main' : 'text.secondary',
        transition: 'background-color 120ms ease, box-shadow 120ms ease',
        '& svg': { fontSize: 20 },
      })}
    >
      {icon}
      {dot && (
        <Box
          component="span"
          aria-hidden
          sx={(theme) => ({
            position: 'absolute', top: 3, right: 3, width: 8, height: 8, borderRadius: '50%',
            bgcolor: 'error.main', boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
          })}
        />
      )}
    </Box>
  );
}

export function RowLabel({ children }) {
  return (
    <Typography variant="body2" noWrap component="span" sx={{ flex: 1, minWidth: 0, color: 'inherit' }}>
      {children}
    </Typography>
  );
}
